import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { X, Save, Eye, Printer, Plus, Trash2, Search } from 'lucide-react';
import { serviceCallsAPI } from '@shared/apiClient';
import { resolveInvoiceData, generateServiceCallPDF, printServiceCallPDF } from '../utils/pdfGenerator';
import InventorySearch from './InventorySearch';
import toast from 'react-hot-toast';

const formatMoney = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
}).format(Number(value || 0));

export default function CspireInvoiceEditor({ serviceCall, isOpen, onClose }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(null);
  const [showInventorySearch, setShowInventorySearch] = useState(false);
  const [inventoryResetKey, setInventoryResetKey] = useState(0);

  useEffect(() => {
    if (isOpen && serviceCall) {
      const initial = resolveInvoiceData(serviceCall);
      setFormData(JSON.parse(JSON.stringify(initial)));
    }
  }, [isOpen, serviceCall]);

  const saveMutation = useMutation(
    (invoiceData) => serviceCallsAPI.updateServiceCall(serviceCall.id, {
      invoiceData,
      invoiceNumber: invoiceData.invoiceNumber || null
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['serviceCall', serviceCall.id]);
        queryClient.invalidateQueries('serviceCalls');
        queryClient.invalidateQueries('priorityServiceCalls');
        queryClient.invalidateQueries('regularServiceCalls');
        toast.success('Invoice saved');
      },
      onError: () => toast.error('Failed to save invoice')
    }
  );

  const subtotal = useMemo(() => {
    if (!formData?.lineItems) return 0;
    return formData.lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 1);
    }, 0);
  }, [formData?.lineItems]);

  const taxRate = formData?.taxRate != null ? Number(formData.taxRate) : 0.07;
  const taxAmount = subtotal * taxRate;
  const grandTotal = subtotal + taxAmount;

  if (!isOpen || !serviceCall || !formData) return null;

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateSiteField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      siteLocation: { ...prev.siteLocation, [field]: value }
    }));
  };

  const updateBillToLine = (index, value) => {
    setFormData((prev) => {
      const billTo = [...(prev.billTo || [])];
      billTo[index] = value;
      return { ...prev, billTo };
    });
  };

  const addBillToLine = () => {
    setFormData((prev) => ({ ...prev, billTo: [...(prev.billTo || []), ''] }));
  };

  const removeBillToLine = (index) => {
    setFormData((prev) => ({
      ...prev,
      billTo: (prev.billTo || []).filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index, field, value) => {
    setFormData((prev) => {
      const lineItems = [...prev.lineItems];
      lineItems[index] = { ...lineItems[index], [field]: value };
      return { ...prev, lineItems };
    });
  };

  const addLineItem = () => {
    setFormData((prev) => ({
      ...prev,
      lineItems: [...(prev.lineItems || []), { itemNumber: '', code: '', description: '', unitPrice: 0, quantity: 1 }]
    }));
  };

  const removeLineItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const handleInventorySelect = (item) => {
    setFormData((prev) => ({
      ...prev,
      lineItems: [...(prev.lineItems || []), {
        itemNumber: item.itemNumber || '',
        code: item.salesTaxCode || '',
        description: item.description || item.itemNumber || '',
        unitPrice: parseFloat(item.price || item.cost || 0),
        quantity: 1
      }]
    }));
    setShowInventorySearch(false);
    setInventoryResetKey(k => k + 1);
  };

  const handleSave = () => saveMutation.mutate(formData);

  const handlePreview = () => {
    try {
      generateServiceCallPDF(serviceCall, formData);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handlePrint = () => {
    try {
      printServiceCallPDF(serviceCall, formData);
      toast.success('Print dialog opened');
    } catch (error) {
      console.error('Error printing PDF:', error);
      toast.error('Failed to print PDF');
    }
  };

  const handleSaveAndPreview = async () => {
    try {
      await saveMutation.mutateAsync(formData);
      generateServiceCallPDF(serviceCall, formData);
      toast.success('Saved & PDF downloaded');
    } catch {
      // error toast handled by mutation
    }
  };

  const inputClass = 'block w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500';
  const labelClass = 'block text-xs font-medium text-gray-600 mb-0.5';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-[60]">
      <div className="relative top-6 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white mb-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Edit Cspire Invoice</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleSave}
              disabled={saveMutation.isLoading}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center space-x-1 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{saveMutation.isLoading ? 'Saving...' : 'Save'}</span>
            </button>
            <button
              onClick={handleSaveAndPreview}
              disabled={saveMutation.isLoading}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-1 disabled:opacity-50"
            >
              <Eye className="h-4 w-4" />
              <span>Save & Preview</span>
            </button>
            <button
              onClick={handlePreview}
              className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center space-x-1"
            >
              <Eye className="h-4 w-4" />
              <span>Preview</span>
            </button>
            <button
              onClick={handlePrint}
              className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center space-x-1"
            >
              <Printer className="h-4 w-4" />
              <span>Print</span>
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-4 max-h-[calc(100vh-160px)] overflow-y-auto pr-1">
          {/* Invoice Number & Date */}
          <div className="bg-gray-50 p-3 rounded-md">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Invoice Info</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Invoice Number</label>
                <input
                  type="text"
                  value={formData.invoiceNumber || ''}
                  onChange={(e) => updateField('invoiceNumber', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Invoice Date</label>
                <input
                  type="text"
                  value={formData.invoiceDate || ''}
                  onChange={(e) => updateField('invoiceDate', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Bill To & Site Location side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 p-3 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700">Bill To</h4>
                <button
                  onClick={addBillToLine}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-0.5"
                >
                  <Plus className="h-3 w-3" />
                  <span>Add Line</span>
                </button>
              </div>
              <div className="space-y-1.5">
                {(formData.billTo || []).map((line, i) => (
                  <div key={i} className="flex items-center space-x-1">
                    <input
                      type="text"
                      value={line}
                      onChange={(e) => updateBillToLine(i, e.target.value)}
                      className={inputClass}
                    />
                    {formData.billTo.length > 1 && (
                      <button onClick={() => removeBillToLine(i)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-md">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Site Location</h4>
              <div className="space-y-1.5">
                <div>
                  <label className={labelClass}>Location Name</label>
                  <input
                    type="text"
                    value={formData.siteLocation?.locationName || ''}
                    onChange={(e) => updateSiteField('locationName', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Site ID</label>
                  <input
                    type="text"
                    value={formData.siteLocation?.siteId || ''}
                    onChange={(e) => updateSiteField('siteId', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Address</label>
                  <input
                    type="text"
                    value={formData.siteLocation?.address || ''}
                    onChange={(e) => updateSiteField('address', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>City, State, Zip</label>
                  <input
                    type="text"
                    value={formData.siteLocation?.cityStateZip || ''}
                    onChange={(e) => updateSiteField('cityStateZip', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Opened By</label>
                  <input
                    type="text"
                    value={formData.siteLocation?.openedBy || ''}
                    onChange={(e) => updateSiteField('openedBy', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Work Order / Generator */}
          <div className="bg-gray-50 p-3 rounded-md">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Work Order & Generator</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Work Order #</label>
                <input
                  type="text"
                  value={formData.workOrderNumber || ''}
                  onChange={(e) => updateField('workOrderNumber', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Generator Model</label>
                <input
                  type="text"
                  value={formData.generatorModel || ''}
                  onChange={(e) => updateField('generatorModel', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Generator Serial #</label>
                <input
                  type="text"
                  value={formData.generatorSerialNumber || ''}
                  onChange={(e) => updateField('generatorSerialNumber', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
              <div>
                <label className={labelClass}>Air filter</label>
                <input
                  type="text"
                  value={formData.airFilter || ''}
                  onChange={(e) => updateField('airFilter', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Fuel filter 1</label>
                <input
                  type="text"
                  value={formData.fuelFilter1 || ''}
                  onChange={(e) => updateField('fuelFilter1', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Fuel filter 2</label>
                <input
                  type="text"
                  value={formData.fuelFilter2 || ''}
                  onChange={(e) => updateField('fuelFilter2', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Oil filter 1</label>
                <input
                  type="text"
                  value={formData.oilFilter1 || ''}
                  onChange={(e) => updateField('oilFilter1', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Oil filter 2</label>
                <input
                  type="text"
                  value={formData.oilFilter2 || ''}
                  onChange={(e) => updateField('oilFilter2', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="bg-gray-50 p-3 rounded-md">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Service Details</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Date of Service</label>
                <input
                  type="text"
                  value={formData.serviceDate || ''}
                  onChange={(e) => updateField('serviceDate', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Hours Run</label>
                <input
                  type="text"
                  value={formData.hoursRun || ''}
                  onChange={(e) => updateField('hoursRun', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Type of Service</label>
                <input
                  type="text"
                  value={formData.serviceType || ''}
                  onChange={(e) => updateField('serviceType', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Work Performed */}
          <div className="bg-gray-50 p-3 rounded-md">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Work Performed</h4>
            <textarea
              value={formData.workPerformed || ''}
              onChange={(e) => updateField('workPerformed', e.target.value)}
              rows={4}
              className={`${inputClass} resize-y`}
            />
          </div>

          {/* Line Items */}
          <div className="bg-gray-50 p-3 rounded-md">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-700">Line Items</h4>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowInventorySearch(!showInventorySearch)}
                  className="text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 flex items-center space-x-0.5"
                >
                  <Search className="h-3 w-3" />
                  <span>From Inventory</span>
                </button>
                <button
                  onClick={addLineItem}
                  className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 flex items-center space-x-0.5"
                >
                  <Plus className="h-3 w-3" />
                  <span>Add Item</span>
                </button>
              </div>
            </div>
            {showInventorySearch && (
              <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded p-3">
                <p className="text-xs text-indigo-700 mb-2">Search inventory to add a line item with pre-filled details.</p>
                <InventorySearch
                  onSelect={handleInventorySelect}
                  placeholder="Search parts by item # or description..."
                  resetKey={inventoryResetKey}
                />
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-300 text-left">
                    <th className="pb-1 pr-2 text-xs font-medium text-gray-600 w-28">Item #</th>
                    <th className="pb-1 pr-2 text-xs font-medium text-gray-600 w-20">Code</th>
                    <th className="pb-1 pr-2 text-xs font-medium text-gray-600">Description</th>
                    <th className="pb-1 pr-2 text-xs font-medium text-gray-600 w-24 text-right">Unit Price</th>
                    <th className="pb-1 pr-2 text-xs font-medium text-gray-600 w-16 text-right">Qty</th>
                    <th className="pb-1 pr-2 text-xs font-medium text-gray-600 w-24 text-right">Amount</th>
                    <th className="pb-1 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(formData.lineItems || []).map((item, i) => {
                    const amount = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 1);
                    return (
                      <tr key={i} className="border-b border-gray-200">
                        <td className="py-1 pr-2">
                          <input
                            type="text"
                            value={item.itemNumber || ''}
                            onChange={(e) => updateLineItem(i, 'itemNumber', e.target.value)}
                            className={inputClass}
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            type="text"
                            value={item.code || ''}
                            onChange={(e) => updateLineItem(i, 'code', e.target.value)}
                            className={inputClass}
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            type="text"
                            value={item.description || ''}
                            onChange={(e) => updateLineItem(i, 'description', e.target.value)}
                            className={inputClass}
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            type="number"
                            step="0.01"
                            value={item.unitPrice ?? 0}
                            onChange={(e) => updateLineItem(i, 'unitPrice', e.target.value)}
                            className={`${inputClass} text-right`}
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            type="number"
                            step="1"
                            value={item.quantity ?? 1}
                            onChange={(e) => updateLineItem(i, 'quantity', e.target.value)}
                            className={`${inputClass} text-right`}
                          />
                        </td>
                        <td className="py-1 pr-2 text-right font-medium text-gray-700">
                          {formatMoney(amount)}
                        </td>
                        <td className="py-1">
                          <button onClick={() => removeLineItem(i)} className="text-red-400 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-3 pt-2 border-t border-gray-300">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <label className={labelClass}>Tax Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={((formData.taxRate ?? 0.07) * 100).toFixed(1)}
                    onChange={(e) => updateField('taxRate', parseFloat(e.target.value) / 100 || 0)}
                    className="w-20 rounded border border-gray-300 px-2 py-1 text-sm text-right focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="text-sm space-y-0.5 text-right">
                  <div className="text-gray-600">
                    Subtotal: <span className="font-medium text-gray-800 ml-2">{formatMoney(subtotal)}</span>
                  </div>
                  <div className="text-gray-600">
                    Tax ({(taxRate * 100).toFixed(1)}%): <span className="font-medium text-gray-800 ml-2">{formatMoney(taxAmount)}</span>
                  </div>
                  <div className="text-gray-900 font-semibold text-base">
                    Grand Total: <span className="ml-2">{formatMoney(grandTotal)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
